import { create } from 'zustand';
import {
    sendPontosBatch,
    sendPontosSnapshot,
    sendPontoUpsert,
    sendPontoDelete,
    sendPontosCodigoDelete,
    sendPontosReset
} from '../channels/pontosItinerarioChannel';

const sortFn = (a, b) =>
    Number(a.SEQUENCIA) - Number(b.SEQUENCIA);

export const usePontosItinerarioStore = create((set, get) => ({
    
    pontos: {},

    codigosComPontos: new Set(),

    setPontosBatch: (
        pontosPorCodigo = {},
        broadcast = true
    ) => {

        const entries = Object.entries(
            pontosPorCodigo || {}
        );

        if (!entries.length) {
            return;
        }

        const normalized = {};

        for (const [codigo, pontos] of entries) {

            const key = String(codigo || '').trim();

            if (!key || !Array.isArray(pontos) || !pontos.length) {
                continue;
            }

            const unique = new Map();

            for (const ponto of pontos) {

                const sequencia =
                    Number(ponto?.SEQUENCIA);

                if (!Number.isInteger(sequencia)) {
                    continue;
                }

                unique.set(
                    sequencia,
                    ponto
                );
            }

            const normalizedPontos =
                [...unique.values()]
                    .sort(sortFn);

            if (normalizedPontos.length) {
                normalized[key] = normalizedPontos;
            }
        }

        if (!Object.keys(normalized).length) {
            return;
        }

        set(state => {

            const codigosComPontos =
                new Set(state.codigosComPontos);

            for (const codigo of Object.keys(normalized)) {
                codigosComPontos.add(codigo);
            }

            return {
                pontos: {
                    ...state.pontos,
                    ...normalized
                },
                codigosComPontos
            };

        });

        if (broadcast) {
            sendPontosBatch(normalized);
        }

    },

    upsertPonto: (
        ponto,
        broadcast = true
    ) => {

        const key = String(
            ponto?.CODIGO_INTEGRACAO || ''
        ).trim();

        const sequencia = Number(
            ponto?.SEQUENCIA
        );

        if (
            !key ||
            !Number.isInteger(sequencia)
        ) {
            return;
        }

        set(state => {

            const current =
                state.pontos[key] || [];

            const next = [...current];

            const index = next.findIndex(
                p =>
                    Number(p.SEQUENCIA) ===
                    sequencia
            );

            if (index >= 0) {

                next[index] = ponto;

            } else {

                next.push(ponto);
            }

            next.sort(sortFn);

            const codigosComPontos =
                new Set(state.codigosComPontos);

            codigosComPontos.add(key);

            return {
                pontos: {
                    ...state.pontos,
                    [key]: next
                },
                codigosComPontos
            };

        });

        if (broadcast) {
            sendPontoUpsert(ponto);
        }

    },

    deletePonto: (
        {
            CODIGO_INTEGRACAO,
            SEQUENCIA
        },
        broadcast = true
    ) => {

        const key = String(
            CODIGO_INTEGRACAO || ''
        ).trim();

        const sequencia =
            Number(SEQUENCIA);

        if (
            !key ||
            !Number.isInteger(sequencia)
        ) {
            return;
        }

        set(state => {

            const next =
                (state.pontos[key] || [])
                    .filter(
                        p =>
                            Number(p.SEQUENCIA) !==
                            sequencia
                    );

            const codigosComPontos =
                new Set(state.codigosComPontos);

            if (next.length) {
                codigosComPontos.add(key);
            } else {
                codigosComPontos.delete(key);
            }

            return {
                pontos: {
                    ...state.pontos,
                    [key]: next
                },
                codigosComPontos
            };

        });

        if (broadcast) {

            sendPontoDelete({
                CODIGO_INTEGRACAO: key,
                SEQUENCIA: sequencia
            });
        }

    },

    deletePontosByCodigo: (
        codigo,
        broadcast = true
    ) => {

        const key = String(codigo || '').trim();

        if (!key) {
            return;
        }

        set(state => {

            if (
                !state.pontos[key] &&
                !state.codigosComPontos.has(key)
            ) {
                return state;
            }

            const pontos = {
                ...state.pontos
            };

            delete pontos[key];

            const codigosComPontos =
                new Set(
                    state.codigosComPontos
                );

            codigosComPontos.delete(key);

            return {
                pontos,
                codigosComPontos
            };

        });

        if (broadcast) {

            sendPontosCodigoDelete(key);

        }

    },

    sendPontosSnapshot: () => {
        sendPontosSnapshot({
            pontos: get().pontos
        });
    },

    setPontosSnapshot: (pontos = {}) => {

        const normalized =
            pontos && typeof pontos === 'object'
                ? pontos
                : {};

        set({
            pontos: normalized,
            codigosComPontos: new Set(
                Object.entries(normalized)
                    .filter(
                        ([, pontos]) =>
                            Array.isArray(pontos) &&
                            pontos.length
                    )
                    .map(([codigo]) => codigo)
            )
        });

    },

    resetPontos: (
        broadcast = true
    ) => {

        set({
            pontos: {},
            codigosComPontos: new Set()
        });

        if (broadcast) {
            sendPontosReset();
        }

    }

}));